/**
 * Variant Image Filter
 * Filters product gallery images based on color variant selection
 * Uses image ALT text in format: color:<variant-value>
 */

class VariantImageFilter {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.gallery = document.getElementById(`MediaGallery-${sectionId}`);
    this.thumbnailSlider = document.getElementById(`GalleryThumbnails-${sectionId}`);
    this.productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);

    if (!this.gallery) return;

    this.init();
  }

  init() {
    // Subscribe to variant change events
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    }

    // Initial filter based on current variant
    this.applyInitialFilter();
  }

  /**
   * Normalize a color value for comparison
   * @param {string} value - The color value to normalize
   * @returns {string} - Normalized lowercase, dash-separated value
   */
  normalize(value) {
    if (!value) return '';
    return value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/_/g, '-');
  }

  /**
   * Find the color option position (1, 2, or 3) from the variant picker
   * @returns {number|null} - The option position or null
   */
  findColorOptionPosition() {
    if (!this.productInfo) return null;

    const variantSelects = this.productInfo.querySelector('variant-selects');
    if (!variantSelects) return null;

    // Look for inputs with name like "Color-1" or "Colour-2"
    const colorInput = variantSelects.querySelector(
      'input[name^="Color-"], input[name^="Colour-"], input[name^="color-"], input[name^="colour-"]'
    );
    if (colorInput) {
      const nameParts = colorInput.name.split('-');
      const position = parseInt(nameParts[nameParts.length - 1], 10);
      if (!isNaN(position)) return position;
    }

    // Check dropdowns - look at the name attribute pattern "options[Color]"
    const colorSelect = variantSelects.querySelector(
      'select[name="options[Color]"], select[name="options[Colour]"], select[name="options[color]"], select[name="options[colour]"]'
    );
    if (colorSelect) {
      // Find position by looking at sibling dropdowns
      const allSelects = Array.from(variantSelects.querySelectorAll('.product-form__input--dropdown select'));
      const index = allSelects.indexOf(colorSelect);
      if (index !== -1) return index + 1;
    }

    // Fallback: Check fieldsets by legend text
    const allInputWrappers = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');
    let position = 0;
    for (const wrapper of allInputWrappers) {
      position++;
      const legend = wrapper.querySelector('legend');
      const label = wrapper.querySelector('label.form__label');
      const textElement = legend || label;

      if (textElement) {
        const optionName = textElement.textContent.split(':')[0].trim().toLowerCase();
        if (optionName === 'color' || optionName === 'colour') {
          return position;
        }
      }
    }

    return null;
  }

  /**
   * Get the currently selected color value
   * @returns {string|null} - The selected color or null
   */
  getSelectedColor() {
    if (!this.productInfo) return null;

    const variantSelects = this.productInfo.querySelector('variant-selects');

    // First, try to get from data-selected-variant JSON
    const variantJson = this.productInfo.querySelector('[data-selected-variant]');
    if (variantJson) {
      try {
        const variant = JSON.parse(variantJson.textContent);
        const colorPosition = this.findColorOptionPosition();
        if (colorPosition && variant) {
          return variant[`option${colorPosition}`];
        }
      } catch (e) {
        // Continue to fallback methods
      }
    }

    if (!variantSelects) return null;

    // Fallback: try to get from checked color input directly
    const colorInput = variantSelects.querySelector(
      'input[name^="Color-"]:checked, input[name^="Colour-"]:checked, input[name^="color-"]:checked, input[name^="colour-"]:checked'
    );
    if (colorInput) {
      return colorInput.value;
    }

    // Check for dropdown selects
    const colorSelect = variantSelects.querySelector(
      'select[name="options[Color]"], select[name="options[Colour]"], select[name="options[color]"], select[name="options[colour]"]'
    );
    if (colorSelect) {
      return colorSelect.value;
    }

    // Last fallback: Check fieldsets by legend text
    const allInputWrappers = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');
    for (const wrapper of allInputWrappers) {
      const legend = wrapper.querySelector('legend');
      const label = wrapper.querySelector('label.form__label');
      const textElement = legend || label;

      if (textElement) {
        const optionName = textElement.textContent.split(':')[0].trim().toLowerCase();
        if (optionName === 'color' || optionName === 'colour') {
          const checkedInput = wrapper.querySelector('input:checked');
          if (checkedInput) return checkedInput.value;

          const select = wrapper.querySelector('select');
          if (select) return select.value;
        }
      }
    }

    return null;
  }

  /**
   * Apply initial filter based on URL variant or selected value
   */
  applyInitialFilter() {
    const selectedColor = this.getSelectedColor();
    if (selectedColor) {
      this.filterByColor(selectedColor);
    }
  }

  /**
   * Handle variant change event
   * @param {Object} event - Pub/sub event object
   */
  onVariantChange(event) {
    if (!event?.data?.variant) return;

    // Only handle events for this section
    if (event.data.sectionId !== this.sectionId) return;

    const variant = event.data.variant;
    const colorPosition = this.findColorOptionPosition();

    if (colorPosition) {
      const colorValue = variant[`option${colorPosition}`];
      if (colorValue) {
        this.filterByColor(colorValue);
      }
    }
  }

  /**
   * Filter gallery images by color
   * @param {string} color - The selected color value
   */
  filterByColor(color) {
    const normalizedColor = this.normalize(color);
    if (!normalizedColor) return;

    let firstVisibleSlide = null;
    let hasVisibleImages = false;

    // Filter main gallery slides
    const slides = this.gallery?.querySelectorAll('.product__media-item') || [];
    slides.forEach((slide) => {
      const imageColor = slide.dataset.imageColor;

      // No color tag = always visible (lifestyle shots)
      if (!imageColor) {
        slide.style.display = '';
        slide.classList.remove('variant-filtered');
        if (!firstVisibleSlide) firstVisibleSlide = slide;
        hasVisibleImages = true;
        return;
      }

      // Check if image color starts with the selected color
      // This allows "pink" to match "pink", "pink-back", "pink-detail"
      const isMatch = imageColor.startsWith(normalizedColor);

      if (isMatch) {
        slide.style.display = '';
        slide.classList.remove('variant-filtered');
        if (!firstVisibleSlide) firstVisibleSlide = slide;
        hasVisibleImages = true;
      } else {
        slide.style.display = 'none';
        slide.classList.add('variant-filtered');
      }
    });

    // Filter thumbnails
    const thumbnails = this.thumbnailSlider?.querySelectorAll('.thumbnail-list__item') || [];
    thumbnails.forEach(thumb => {
      const imageColor = thumb.dataset.imageColor;

      if (!imageColor) {
        thumb.style.display = '';
        thumb.classList.remove('variant-filtered');
        return;
      }

      const isMatch = imageColor.startsWith(normalizedColor);
      if (isMatch) {
        thumb.style.display = '';
        thumb.classList.remove('variant-filtered');
      } else {
        thumb.style.display = 'none';
        thumb.classList.add('variant-filtered');
      }
    });

    // Set first visible slide as active
    if (firstVisibleSlide && hasVisibleImages) {
      this.setActiveSlide(firstVisibleSlide);
    }

    // Update slider counter if present
    this.updateSliderCounter();
  }

  /**
   * Set a slide as the active slide
   * @param {Element} slide - The slide element to activate
   */
  setActiveSlide(slide) {
    if (!slide) return;

    // Remove active state from all slides
    const allSlides = this.gallery?.querySelectorAll('.product__media-item') || [];
    allSlides.forEach(s => s.classList.remove('is-active'));

    // Set new active slide
    slide.classList.add('is-active');

    // Update thumbnail active state
    const mediaId = slide.dataset.mediaId;
    if (mediaId && this.thumbnailSlider) {
      const thumbnails = this.thumbnailSlider.querySelectorAll('button.thumbnail');
      thumbnails.forEach(thumb => {
        const thumbTarget = thumb.closest('li')?.dataset.target;
        thumb.setAttribute('aria-current', thumbTarget === mediaId ? 'true' : 'false');
      });
    }

    // Scroll slider to first visible slide on mobile
    const sliderComponent = this.gallery?.querySelector('slider-component');
    if (sliderComponent) {
      const slider = sliderComponent.querySelector('.slider');
      if (slider) {
        slider.scrollTo({
          left: slide.offsetLeft,
          behavior: 'instant'
        });
      }
    }
  }

  /**
   * Update the slider counter to reflect visible slides
   */
  updateSliderCounter() {
    const counter = this.gallery?.querySelector('.slider-counter--total');
    if (!counter) return;

    const visibleSlides = this.gallery.querySelectorAll('.product__media-item:not([style*="display: none"])');
    counter.textContent = visibleSlides.length;
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  // Find all product info sections and initialize filter for each
  document.querySelectorAll('product-info').forEach(productInfo => {
    const sectionId = productInfo.dataset.section;
    if (sectionId) {
      new VariantImageFilter(sectionId);
    }
  });
});
